import React, { CSSProperties, useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Config } from '../../consts/config';

declare global {
  interface Window {
    userId: string;
    flavor: string;
    token: string;
    batchId: number;
    chatType: string;
    chatUserId: string;
    sessionId: string;
    onSparklChatInitialized?: (screen: string) => void;
    onSparklNewChatMessage?: (fromId?: string, newMessage?: string) => void;
    onSparklTyping?: () => void;
    onSparklImageEmbadded?: (imageUrl: string) => void;
    onSparklChatMessageRead?: (firebaseUid: string) => void;
    onSparklUnreadCount?: (count: number) => void;
    onSparklSearchMatchCount?: (count: number) => void;
    onSparklChatScreenChange?: (screen: string, teacherData?: any) => void;
    __sparklFilterChat?: (query: string) => void;
    __sparklFilterChatMessages?: (query: string) => void;
    __sparklGoBack?: () => void;
    __sparklChatQuery?: string;
    __sparklChatMessageQuery?: string;
    _flutter?: {
      loader?: {
        load: (options: any) => Promise<any> | void;
      };
    };
  }
}

export interface ChatProps {
  dashboard?: boolean;
  batchId?: number;
  firebaseId?: string;
  sessionId?: string;
  layout?: string;
}

export const Chat: React.FC<ChatProps> = ({
  dashboard = true,
  batchId,
  firebaseId,
  sessionId,
  layout = 'single',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [chatLoaded, setChatLoaded] = useState(false);

  // Active Screen tracking ('chatListing' vs 'chatWindow')
  const [currentScreen, setCurrentScreen] = useState<'chatListing' | 'chatWindow'>('chatListing');
  const [activeTeacher, setActiveTeacher] = useState<{ id?: string; name: string; image?: string } | null>(null);

  // Search feature states
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isTingPlayingRef = useRef(false);
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(true);
  const isVisibleRef = useRef(isVisible);
  const lastUnreadCountRef = useRef<number | null>(null);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  const ting = useCallback(() => {
    if (!isTingPlayingRef.current) {
      try {
        const audio = new Audio('/chat/assets/assets/get_message_tone.mp3');
        audio.play().catch((err) => {
          console.debug('Chat audio playback deferred by browser autoplay policy:', err);
        });
        isTingPlayingRef.current = true;
        const timer = setTimeout(() => {
          isTingPlayingRef.current = false;
        }, 3000);
        timeoutsRef.current.push(timer);
      } catch (e) {
        console.debug('Chat audio initialization error:', e);
      }
    }
  }, []);

  const readKey = useCallback((key: string): string => {
    try {
      const val = localStorage.getItem(key);
      if (!val) return '';
      try {
        const parsed = JSON.parse(val);
        return typeof parsed === 'string' ? parsed : String(parsed);
      } catch {
        return val;
      }
    } catch {
      return '';
    }
  }, []);

  const resolveCredentials = useCallback(() => {
    let id = '';
    let token = '';

    // 1. Try reading from localStorage.user (SSO or full user object)
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        let parsed: any;
        try {
          parsed = JSON.parse(userStr);
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
        } catch {
          parsed = null;
        }

        if (parsed) {
          id =
            parsed?.user?.id?.toString() ||
            parsed?.user_id?.toString() ||
            parsed?.id?.toString() ||
            '';
          token =
            parsed?.token ||
            parsed?.access_token ||
            parsed?.accessToken ||
            '';
        }
      }
    } catch (e) {
      console.debug('Chat: parsing localStorage.user', e);
    }

    // 2. Fallback to individual sparkl-fe auth keys
    if (!id) {
      id = readKey('user_id');
    }
    if (!token) {
      token = readKey('access_token') || readKey('auth');
    }

    return {
      id: id || '0',
      token: token || '',
    };
  }, [readKey]);

  const connect = useCallback(async () => {
    if (isConnectingRef.current) return;
    isConnectingRef.current = true;

    try {
      const { id, token } = resolveCredentials();

      // Ensure globals are assigned immediately for Dart runtime
      window.userId = id;
      window.flavor = 'student';
      window.token = token;
      window.batchId = batchId || 1;
      window.chatType = layout === 'single' ? '' : 'session';
      window.sessionId = (sessionId || '0').toString();
      window.chatUserId = firebaseId || '';
      (window as any).SPARKL_API_BASE_URL =
        Config.BACKEND_DOMAIN || Config.API_URL?.replace(/\/api\/?$/, '') || '';

      const waitForLoader = async (): Promise<boolean> => {
        for (let i = 0; i < 40; i++) {
          if (window._flutter?.loader?.load) return true;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return false;
      };

      const loaderAvailable = await waitForLoader();
      if (!loaderAvailable || !isMountedRef.current) {
        console.warn('Chat: _flutter.loader.load not available yet');
        return;
      }

      const hostElem = document.getElementById('flutter_host');
      if (!hostElem) {
        console.warn('Chat: DOM element #flutter_host not found');
        return;
      }

      // If reconnecting/reloading, remove any stale glass-pane to allow clean mount
      if (hostElem.querySelector('flt-glass-pane')) {
        hostElem.innerHTML = '';
      }

      window._flutter!.loader!.load({
        serviceWorkerSettings: {
          serviceWorkerVersion: '3161997371',
        },
        config: {
          hostElement: hostElem,
          assetBase: '/chat/',
          debugShowSemanticNodes: false,
        },
      });
    } catch (error) {
      console.error('Chat: error during Flutter connect', error);
    } finally {
      isConnectingRef.current = false;
    }
  }, [batchId, firebaseId, layout, resolveCredentials, sessionId]);

  // Context-aware search filtering: searches teachers on listing screen, or messages on active teacher chat
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (currentScreen === 'chatWindow') {
        if (typeof window.__sparklFilterChatMessages === 'function') {
          window.__sparklFilterChatMessages(query);
        }
      } else {
        if (typeof window.__sparklFilterChat === 'function') {
          window.__sparklFilterChat(query);
        }
      }
    },
    [currentScreen]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
  };

  const handleCloseSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setMatchCount(null);
    if (currentScreen === 'chatWindow') {
      if (typeof window.__sparklFilterChatMessages === 'function') {
        window.__sparklFilterChatMessages('');
      }
    } else {
      if (typeof window.__sparklFilterChat === 'function') {
        window.__sparklFilterChat('');
      }
    }
  };

  const handleOpenSearch = () => {
    setIsSearching(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleGoBack = () => {
    setSearchQuery('');
    setIsSearching(false);
    setMatchCount(null);
    if (typeof window.__sparklFilterChatMessages === 'function') {
      window.__sparklFilterChatMessages('');
    }
    if (typeof window.__sparklGoBack === 'function') {
      window.__sparklGoBack();
    } else {
      setCurrentScreen('chatListing');
      setActiveTeacher(null);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    const { id, token } = resolveCredentials();
    window.userId = id;
    window.flavor = 'student';
    window.token = token;
    window.batchId = batchId || 1;
    window.chatType = layout === 'single' ? '' : 'session';
    window.sessionId = (sessionId || '0').toString();
    window.chatUserId = firebaseId || '';

    window.onSparklChatInitialized = (screen: string) => {
      console.log('chat-logs', 'Chat App is ready', screen);
      if (!isMountedRef.current) return;
      setChatLoaded(true);
      if (screen === 'chatWindow') {
        setCurrentScreen('chatWindow');
      } else if (screen === 'chatListing') {
        setCurrentScreen('chatListing');
      }
    };

    window.onSparklChatScreenChange = (screen: string, teacherData?: any) => {
      console.log('chat-logs', 'Chat screen changed to:', screen, teacherData);
      if (!isMountedRef.current) return;
      setCurrentScreen(screen === 'chatWindow' ? 'chatWindow' : 'chatListing');
      if (screen === 'chatWindow' && teacherData) {
        setActiveTeacher(teacherData);
      } else {
        setActiveTeacher(null);
      }
      setSearchQuery('');
      setIsSearching(false);
      setMatchCount(null);
      if (typeof window.__sparklFilterChat === 'function') {
        window.__sparklFilterChat('');
      }
      if (typeof window.__sparklFilterChatMessages === 'function') {
        window.__sparklFilterChatMessages('');
      }
    };

    window.onSparklUnreadCount = (count: number) => {
      console.log('chat-logs', 'Unread count updated:', count);
      if (!isMountedRef.current) return;
      const safeCount = Math.max(0, count || 0);

      if (isVisibleRef.current) {
        setNewMessageCount(0);
        lastUnreadCountRef.current = 0;
        return;
      }

      const prev = lastUnreadCountRef.current;
      lastUnreadCountRef.current = safeCount;

      if (prev !== null && safeCount > prev) {
        ting();
      }

      setNewMessageCount(safeCount);
    };

    window.onSparklSearchMatchCount = (count: number) => {
      if (isMountedRef.current) {
        setMatchCount(count);
      }
    };

    window.onSparklNewChatMessage = (fromId?: string, newMessage?: string) => {
      console.log('chat-logs', 'New Message came from ' + fromId + ': ' + newMessage);
      if (!isVisibleRef.current && isMountedRef.current) {
        ting();
        setNewMessageCount((prev) => {
          const next = prev + 1;
          lastUnreadCountRef.current = next;
          return next;
        });
      }
    };

    window.onSparklTyping = () => {
      console.log('chat-logs', 'The other user is typing right now');
    };

    window.onSparklImageEmbadded = (imageUrl: string) => {
      console.log('chat-logs', 'Image received:', imageUrl);
      if (imageUrl && typeof imageUrl === 'string') {
        try {
          window.open(imageUrl, '_blank', 'noopener,noreferrer');
        } catch (e) {
          console.debug('Chat image open error:', e);
        }
      }
    };

    window.onSparklChatMessageRead = (messageId: string) => {
      console.log('chat-logs', 'Message read: ' + messageId);
      if (isMountedRef.current) {
        setNewMessageCount(0);
        lastUnreadCountRef.current = 0;
      }
    };

    connect();

    const fallbackTimer = setTimeout(() => {
      if (isMountedRef.current && !chatLoaded) {
        setChatLoaded(true);
      }
    }, 3500);
    timeoutsRef.current.push(fallbackTimer);

    return () => {
      isMountedRef.current = false;
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
      delete window.onSparklChatInitialized;
      delete window.onSparklNewChatMessage;
      delete window.onSparklTyping;
      delete window.onSparklImageEmbadded;
      delete window.onSparklChatMessageRead;
      delete window.onSparklUnreadCount;
      delete window.onSparklSearchMatchCount;
      delete window.onSparklChatScreenChange;
      if (typeof window.__sparklFilterChat === 'function') {
        window.__sparklFilterChat('');
      }
      if (typeof window.__sparklFilterChatMessages === 'function') {
        window.__sparklFilterChatMessages('');
      }
    };
  }, [batchId, chatLoaded, connect, firebaseId, layout, resolveCredentials, sessionId, ting]);

  const toggleVisibility = () => {
    setIsVisible((prev) => {
      const next = !prev;
      if (next) {
        setNewMessageCount(0);
        lastUnreadCountRef.current = 0;
        if (!chatLoaded) {
          connect();
        }
      } else {
        setSearchQuery('');
        setIsSearching(false);
        setMatchCount(null);
        if (typeof window.__sparklFilterChat === 'function') {
          window.__sparklFilterChat('');
        }
        if (typeof window.__sparklFilterChatMessages === 'function') {
          window.__sparklFilterChatMessages('');
        }
      }
      return next;
    });
  };

  const styleChatWrapper: CSSProperties = {
    position: 'fixed',
    right: '24px',
    bottom: '24px',
    zIndex: 99999,
  };

  const styleChatWindow: CSSProperties = {
    width: '350px',
    height: '530px',
    boxSizing: 'border-box',
    border: '1px solid rgba(233, 235, 236, 0.25)',
    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35)',
    backgroundColor: '#1E1E1E',
    position: 'fixed',
    bottom: '92px',
    right: '24px',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    borderBottomLeftRadius: '20px',
    borderBottomRightRadius: '4px',
    overflow: 'hidden',
    zIndex: 99998,
    visibility: isVisible ? 'visible' : 'hidden',
    opacity: isVisible ? 1 : 0,
    pointerEvents: isVisible ? 'auto' : 'none',
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
  };

  // Increased padding as requested for Image 1 view (chatListing)
  // Compact, sleek padding for Image 2 view (chatWindow)
  const isChatWindow = currentScreen === 'chatWindow';

  const styleTitleContainer: CSSProperties = {
    height: isChatWindow ? '54px' : '68px',
    minHeight: isChatWindow ? '54px' : '68px',
    backgroundColor: '#181818',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: isChatWindow ? '6px 14px' : '16px 20px',
    boxSizing: 'border-box',
    zIndex: 10,
    transition: 'height 0.2s ease, padding 0.2s ease',
  };

  const styleTitleText: CSSProperties = {
    fontSize: '24px',
    fontWeight: 600,
    color: '#ffffff',
    letterSpacing: '-0.8px',
    margin: 0,
    userSelect: 'none',
  };

  const styleHeaderActions: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: isChatWindow ? '6px' : '10px',
  };

  const styleHeaderIconBtn: CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.85)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: isChatWindow ? '6px' : '8px',
    borderRadius: '8px',
    outline: 'none',
    transition: 'color 0.2s, background-color 0.2s',
  };

  const styleBackBtn: CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    margin: 0,
    marginLeft: '-4px',
    borderRadius: '8px',
    outline: 'none',
    transition: 'background-color 0.2s',
    flexShrink: 0,
  };

  const styleTeacherInfo: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
    overflow: 'hidden',
    justifyContent: 'flex-start',
  };

  const styleTeacherAvatar: CSSProperties = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    objectFit: 'cover',
    backgroundColor: '#333333',
    border: '1.5px solid rgba(255, 255, 255, 0.15)',
    flexShrink: 0,
  };

  const styleTeacherName: CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#ffffff',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    letterSpacing: '-0.2px',
  };

  const styleSearchContainer: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '6px 12px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  };

  const styleSearchInput: CSSProperties = {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#ffffff',
    fontSize: '14px',
    width: '100%',
  };

  const styleFlutterHost: CSSProperties = {
    width: '100%',
    flex: 1,
    height: isChatWindow ? 'calc(100% - 54px)' : 'calc(100% - 68px)',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#1E1E1E',
  };

  const styleEmptySearch: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#1E1E1E',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '13px',
    padding: '20px',
    textAlign: 'center',
    zIndex: 5,
    pointerEvents: 'none',
  };

  const styleChatButton: CSSProperties = {
    width: '58px',
    height: '58px',
    borderRadius: '29px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #FDB813 0%, #F58220 100%)',
    boxShadow: '0 6px 20px rgba(245, 130, 32, 0.55)',
    border: 'none',
    outline: 'none',
    color: '#ffffff',
    position: 'relative',
    zIndex: 99999,
  };

  const styleBadge: CSSProperties = {
    position: 'absolute',
    top: '-3px',
    right: '-3px',
    backgroundColor: '#E53935',
    color: '#ffffff',
    borderRadius: '10px',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 'bold',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    border: '2px solid #ffffff',
  };

  const avatarSrc =
    activeTeacher?.image && activeTeacher.image.length > 5
      ? activeTeacher.image.startsWith('http') || activeTeacher.image.startsWith('/')
        ? activeTeacher.image
        : `/chat/assets/assets/${activeTeacher.image}`
      : '/chat/assets/assets/icons/app_icon.png';

  return (
    <div style={styleChatWrapper}>
      {/* Chat Window Card (Unified Header Container + Flutter Engine Host) */}
      <div style={styleChatWindow}>
        {/* Header Container (Increased padding in Image 1 view, active teacher UI in Image 2 view) */}
        <div style={styleTitleContainer}>
          {isSearching ? (
            <div style={styleSearchContainer}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(255, 255, 255, 0.7)"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                placeholder={
                  currentScreen === 'chatWindow'
                    ? 'Search messages in this chat...'
                    : 'Search chats...'
                }
                aria-label="Search chats"
                style={styleSearchInput}
                onChange={handleSearchChange}
              />
              <button
                type="button"
                style={styleHeaderIconBtn}
                title="Clear & Close Search"
                aria-label="Clear & Close Search"
                onClick={handleCloseSearch}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          ) : currentScreen === 'chatWindow' ? (
            /* Image 2 View: Active Teacher Chat Header */
            <>
              <div style={styleTeacherInfo}>
                <button
                  type="button"
                  style={styleBackBtn}
                  title="Back to all chats"
                  aria-label="Back to all chats"
                  onClick={handleGoBack}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <img
                  src={avatarSrc}
                  alt={activeTeacher?.name || 'Teacher'}
                  style={styleTeacherAvatar}
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
                <h3 style={styleTeacherName}>
                  {activeTeacher?.name || 'Teacher Chat'}
                </h3>
              </div>
              <div style={styleHeaderActions}>
                {/* Search Toggle Icon for active chat messages */}
                <button
                  type="button"
                  style={styleHeaderIconBtn}
                  title="Search messages in this chat"
                  aria-label="Search messages in this chat"
                  onClick={handleOpenSearch}
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>
            </>
          ) : (
            /* Image 1 View: Chat Listing Header with generous padding */
            <>
              <h3 style={styleTitleText}>Chats</h3>
              <div style={styleHeaderActions}>
                {/* Search Toggle Icon */}
                <button
                  type="button"
                  style={styleHeaderIconBtn}
                  title="Search chats"
                  aria-label="Search chats"
                  onClick={handleOpenSearch}
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>

                {/* Reconnect / Refresh Icon */}
                <button
                  type="button"
                  style={styleHeaderIconBtn}
                  title="Reconnect Chat"
                  aria-label="Reconnect Chat"
                  onClick={(e) => {
                    e.stopPropagation();
                    connect();
                  }}
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Flutter Web Container - element dedicated to Flutter runtime */}
        <div id="flutter_host" style={styleFlutterHost}>
          {/* Empty search results feedback */}
          {isSearching && searchQuery.trim() !== '' && matchCount === 0 && (
            <div style={styleEmptySearch}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(255, 255, 255, 0.4)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginBottom: '8px' }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <span>
                {currentScreen === 'chatWindow'
                  ? `No messages found matching "${searchQuery}"`
                  : `No chats found matching "${searchQuery}"`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Toggle Button */}
      <motion.button
        type="button"
        id="sparkl-chat-toggle-button"
        aria-label={isVisible ? 'Close Chat' : 'Open Chat'}
        style={styleChatButton}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        onClick={toggleVisibility}
      >
        {isVisible ? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        )}

        {newMessageCount > 0 && !isVisible && (
          <div style={styleBadge}>
            {newMessageCount > 99 ? '99+' : newMessageCount}
          </div>
        )}
      </motion.button>
    </div>
  );
};

export default Chat;
